import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import Map from '../../../../../../components/Map/components/SimpleMap'
import { Button, DynamicTooltip, Modal, Select, TextArea } from "@gpstrackit/gtc-react-controls";
import { Divider, Grid, Typography } from "@mui/material";

import DriverInfo from '../../modals/driverInfo'
import AssetInfo from "../../modals/assetInfo";
import { Timezone } from 'Components/uiControls';

//Helpers
import { getLightMetricEvent, getVidFleetEvent } from "Core/data/VideoEvents";
import { checkDeviceData } from "Core/data/Helpers";
import { getEventIcon } from "Modules/driverPerformance/.globals/helpers";

//Components
import { NotificationContainer, NotificationManager } from "Components/Notifications";

//Actions
import { openVideoAssignDriverModal } from "Modules/.common/components/VideoAssignDriver/redux/slice";
import { updateEventAssignStatus } from '../../redux/slice';
import { catalogLoad } from "Redux/actions";
import { createDeviceReadingExtraField, getDriverPerformanceEventVideoDetail, reset, resetCreateDeviceReadingExtraField, updateEventExtraFieldsLocally } from "./redux/slice";
import { assignStatusesToEvent, reset as resetAssignStatus, searchStatuses } from '../assingStatus/redux/slice';

// Video Components
import VidFleetVideo from "Modules/.common/components/VidFleetVideo";
import SmartWitnessSnapshot from "Modules/.common/components/SmartWitnessSnapshot";

//Styles
import "./index.scss";

const EditEvent = (props) => {

    //Language
    const { messages, locale } = useIntl();

    //Redux
    const dispatch = useDispatch();

    //State
    const [openImageModal, setOpenImageModal] = useState(false);
    const [status, setStatus] = useState(null);
    const [initialStatus, setInitialStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [editingComment, setEditingComment] = useState(false);
    const [hasNewComment, setHasNewComment] = useState(false);
    const [localStatuses, setLocalStatuses] = useState([]);
    const [isSavingBoth, setIsSavingBoth] = useState(false);

    //Redux state
    const { open, loading, event, data } = useSelector((state) => state.driverPerformanceEventsTabEditEventRedux);
    const user = useSelector(state => state?.securityRedux?.user || {});
    const { dateformat, timeZone } = user;
    const statuses = useSelector((state) => state.driverPerformanceEventsTabAssignStatusSliceRedux.statuses);
    const loadingStatuses = useSelector((state) => state.driverPerformanceEventsTabAssignStatusSliceRedux.loading);
    const savedStatus = useSelector((state) => state.driverPerformanceEventsTabAssignStatusSliceRedux.savedStatus);
    const statusError = useSelector((state) => state.driverPerformanceEventsTabAssignStatusSliceRedux.error);
    const createCommentLoading = useSelector((state) => state.driverPerformanceEventsTabEditEventRedux.loadingCreateDeviceReadingExtraField);
    const createCommentError = useSelector((state) => state.driverPerformanceEventsTabEditEventRedux.createCommentError);
    const createCommentSuccess = useSelector((state) => state.driverPerformanceEventsTabEditEventRedux.createCommentSuccess);
    const createDeviceReadingExtraFieldState = useSelector((state) => state.driverPerformanceEventsTabEditEventRedux.createDeviceReadingExtraField);
    const chatUsers = useSelector((state) => state.catalogsRedux.chatUsers);

    //Handlers and utils
    const handleClose = useCallback(() => {
        setOpenImageModal(false);
        dispatch(reset());
    }, []);

    //Effects
    useEffect(() => {
        if (event?.rowId && event?.deviceId && open) {
            dispatch(getDriverPerformanceEventVideoDetail({
                rowId: event.rowId,
                deviceId: event.deviceId,
                messages,
                linkedDevice: event?.linkedDevice || null
            }));
        }
    }, [open, event, messages, dispatch]);

    // Fetch statuses on mount
    useEffect(() => {
        dispatch(searchStatuses());
        if (!chatUsers || chatUsers.length === 0) {
            dispatch(catalogLoad(['chatUsers']));
        }
    }, []);

    // Sync statuses from Redux to local state
    useEffect(() => {
        if (statuses?.length) {
            setLocalStatuses(statuses);
        }
    }, [statuses]);

    // Close image modal when modal closes
    useEffect(() => {
        if (!open) {
            setOpenImageModal(false);
        }
    }, [open]);

    // Initialize status from event data
    useEffect(() => {
        if (event && localStatuses?.length) {
            try {
                let deviceData = checkDeviceData(event?.deviceData || '{}');
                if (deviceData?.caseEventStatus) {
                    let option = localStatuses?.find(status => status.entityId === deviceData?.caseEventStatus);
                    setStatus(option);
                    setInitialStatus(option);
                }
            } catch (error) { }
        }
    }, [event, localStatuses]);

    // Handle status save result
    useEffect(() => {
        if (savedStatus || statusError) {
            if (statusError) {
                NotificationManager.error(
                    messages["driver_performance_error_saving"],
                    messages["error"],
                    3000
                );
                setStatusLoading(false);
                setIsSavingBoth(false);
            } else if (savedStatus?.success) {
                // Only show notification if we're NOT saving both (status + comment)
                // If saving both, the comment effect will show the notification
                if (!isSavingBoth) {
                    NotificationManager.success(
                        messages['driver_performance_changes_saved_successfully'],
                        messages["success"],
                        3000
                    );
                    setStatusLoading(false);
                }
                // If saving both, loading will be stopped by comment effect

                // Update status on event list
                dispatch(updateEventAssignStatus({
                    rowId: event?.rowId,
                    status: status,
                    esn: event?.esn
                }));

                // Update initialStatus to match the saved status so button becomes disabled
                setInitialStatus(status);

                // Close modal if status is falsepositive or noissue (event removed from list)
                const shouldCloseModal = status?.entityId === 'falsepositive' || status?.entityId === 'noissue';
                if (shouldCloseModal) {
                    handleClose();
                }
            }
            dispatch(resetAssignStatus());
        }
    }, [savedStatus, statusError, hasNewComment, commentText, messages, dispatch, event, status, handleClose]);

    let eventType = event?.unitType === "lightmetrics" ? getLightMetricEvent(event?.eventCode) : getVidFleetEvent(event?.eventCode);

    // Parse deviceData to get status, disputed flag, and severity
    const getEventStatusData = () => {
        let eventStatus = null;
        let isDisputed = false;
        let eventSeverity = null;

        if (event?.deviceData) {
            try {
                let deviceData = checkDeviceData(event.deviceData);
                eventStatus = deviceData?.caseEventStatus;
                if (typeof eventStatus === 'object' && eventStatus !== null) {
                    eventStatus = eventStatus.id || null;
                }
                // Check disputed from deviceData (caseEventDisputed) or from event level
                isDisputed = deviceData?.caseEventDisputed || event?.disputed || false;
                eventSeverity = deviceData?.eventLevel || null;
            } catch (e) {
                eventStatus = null;
                isDisputed = event?.disputed || false;
                eventSeverity = null;
            }
        }
        return { eventStatus, isDisputed, eventSeverity };
    };

    const { eventStatus, isDisputed, eventSeverity } = getEventStatusData();

    // Calculate icon based on current selected status or original event status
    const eventIcon = useMemo(() => {
        // Use the selected status if it exists and is different from initial, otherwise use original eventStatus
        const currentStatus = status?.entityId || eventStatus;
        return getEventIcon(currentStatus, isDisputed, messages);
    }, [status, eventStatus, isDisputed, messages]);

    // Process event comments from extraFields
    const eventComments = useMemo(() => {
        if (!data?.extraFields) return [];

        return data.extraFields
            .filter(field => field.fieldName === 'noteDriverCase')
            .map(field => {
                try {
                    // jsonValue already comes parsed from the query
                    const parsedValue = typeof field.jsonValue === 'string'
                        ? JSON.parse(field.jsonValue)
                        : field.jsonValue;
                    return {
                        id: field.id,
                        comment: parsedValue.description || '',
                        userName: parsedValue.type || 'admin',
                        createdOn: field.createdOn,
                        createdBy: field.createdBy
                    };
                } catch (e) {
                    return null;
                }
            })
            .filter(comment => comment !== null)
            .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
    }, [data?.extraFields]);

    // Mark that there's a new comment when user types
    useEffect(() => {
        if (commentText.trim() && editingComment) {
            setHasNewComment(true);
        } else if (!commentText.trim()) {
            setHasNewComment(false);
        }
    }, [commentText, editingComment]);

    // Effect to handle comment save response
    useEffect(() => {
        if (createCommentSuccess) {
            // Update extraFields locally instead of refreshing entire event
            dispatch(updateEventExtraFieldsLocally({
                newExtraField: createDeviceReadingExtraFieldState
            }));

            // Show notification (whether saving only comment or both)
            NotificationManager.success(
                messages['driver_performance_changes_saved_successfully'],
                messages["success"],
                3000
            );

            // Reset comment state to initial
            setCommentText('');
            setEditingComment(false);
            setHasNewComment(false);
            setStatusLoading(false);
            setIsSavingBoth(false);
            dispatch(resetCreateDeviceReadingExtraField());
        }
        if (createCommentError) {
            NotificationManager.error(
                messages["driver_performance_error_saving"],
                messages["error"],
                3000
            );

            dispatch(resetCreateDeviceReadingExtraField());
            setStatusLoading(false);
            setIsSavingBoth(false);
        }
    }, [createCommentSuccess, createCommentError, createDeviceReadingExtraFieldState, dispatch, messages]);

    // Handle saving changes (status and/or comment)
    const handleSaveChanges = useCallback(() => {
        const hasStatusChanged = status && status?.entityId !== initialStatus?.entityId;
        const hasCommentToSave = hasNewComment && commentText.trim();

        if (!hasStatusChanged && !hasCommentToSave) return;

        // Set flag if saving both
        if (hasStatusChanged && hasCommentToSave) {
            setIsSavingBoth(true);
        }

        setStatusLoading(true);

        // Save comment if there's a new one
        if (hasCommentToSave) {
            dispatch(createDeviceReadingExtraField({
                rowId: event?.rowId,
                deviceId: event?.deviceId,
                fieldName: 'noteDriverCase',
                jsonValue: {
                    type: 'admin',
                    description: commentText
                },
                visibleToDriver: false
            }));
        }

        // Update status if it changed
        if (hasStatusChanged) {
            dispatch(assignStatusesToEvent({
                rowId: event?.rowId,
                status: status,
                esn: event?.esn
            }));
        }
        if (!hasStatusChanged && hasCommentToSave) {
            // Loading will be stopped by createCommentSuccess effect
        }
    }, [status, initialStatus, hasNewComment, commentText, event, dispatch]);

    // Memoize map data to prevent unnecessary re-renders
    const mapData = useMemo(() => ({
        coords: {
            lat: event?.latitude,
            lng: event?.longitude,
        },
        eventName: event?.eventName,
        eventCode: event?.eventCode,
        heading: event?.heading,
    }), [event?.latitude, event?.longitude, event?.eventName, event?.eventCode, event?.heading]);

    return (
        <Modal
            size="small"
            open={open}
            loading={loading || createCommentLoading || loadingStatuses}
            footer={
                <div className="edit-event-modal-footer" style={{ marginLeft: 10 }}>
                    <Button
                        variant="secondary"
                        className="edit-event-modal-footer__cancel-btn"
                        onClick={() => handleClose()}
                        isDisabled={loading}
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        variant="primary"
                        isDisabled={
                            statusLoading ||
                            loading ||
                            (!status || status?.entityId === initialStatus?.entityId) && (!hasNewComment || !commentText.trim())
                        }
                        onClick={handleSaveChanges}
                    >
                        {messages['save']}
                    </Button>
                </div>
            }
        >
            <div className="gtc-rc-modal-header gtc-rc-modal-header-small" style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                <div className="gtc-rc-modal-title" style={{ width: 'calc(100% - 34px)', display: 'flex', alignItems: 'center' }}>
                    <img
                        src={eventType?.icon}
                        alt={eventType?.name}
                        style={{ width: 35, height: 35, marginRight: 5 }}
                    />
                    {event?.eventName ? messages[`driver_performance_${event?.eventName?.replace(/\s+/g, "")}`] || event?.eventName : ''}
                </div>
                <div className="gtc-rc-modal-close-button" style={{ marginRight: 8 }}>
                    <button onClick={() => handleClose()} className="MuiButtonBase-root MuiIconButton-root MuiIconButton-sizeSmall icon-button-gtc-rc gtc-rc-btn-close css-1j7qk7u" >
                        <span className="material-icons notranslate MuiIcon-root MuiIcon-fontSizeSmall icons-gtc-rc gtc-rc-btn-close css-xrdhw3">close</span>
                        <span className="MuiTouchRipple-root css-w0pj6f"></span>
                    </button>
                </div>
            </div>
            <div className="edit-event-modal-content">
                {
                    (!loading && data) ?
                        <table className="edit-event-modal-info">
                            <tbody>
                                <tr className="edit-event-modal-info__row">
                                    <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_event_type"]}:</td>
                                    <td className="edit-event-modal-info__value">
                                        {event?.eventName ? messages[`driver_performance_${event?.eventName?.replace(/\s+/g, "")}`] || event?.eventName : 'N/A'}
                                    </td>
                                </tr>
                                {eventSeverity && (
                                    <tr className="edit-event-modal-info__row">
                                        <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_severity"]}:</td>
                                        <td className="edit-event-modal-info__value">
                                            {messages[`driver_performance_severity_${eventSeverity.toLowerCase()}`] || eventSeverity}
                                        </td>
                                    </tr>
                                )}
                                <tr className="edit-event-modal-info__row">
                                    <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_category"]}:</td>
                                    <td className="edit-event-modal-info__value">
                                        <a className="edit-event-modal-label-tag">{messages["driver_performance_events_drawer_risk"]}</a>
                                    </td>
                                </tr>
                                <tr className="edit-event-modal-info__row">
                                    <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_source"]}:</td>
                                    <td className="edit-event-modal-info__value">
                                        <a className="edit-event-modal-label-tag">{messages["driver_performance_events_drawer_camera"]}</a>
                                    </td>
                                </tr>
                                <tr className="edit-event-modal-info__row">
                                    <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_when"]}:</td>
                                    <td className="event-container-timezone">
                                        <Timezone
                                            date={data?.unitTime}
                                            dateFormat={dateformat}
                                            timeFormat="hh:mm a"
                                            timezone={timeZone}
                                        />
                                    </td>
                                </tr>
                                <tr className="edit-event-modal-info__row">
                                    <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_driver"]}:</td>
                                    <td className="edit-event-modal-info__value">
                                        {
                                            data?.driverName ? (
                                                <DynamicTooltip
                                                    key="driverInfo"
                                                    trigger={<p className="edit-event-link">{data?.driverName}</p>}
                                                    openOn="click"
                                                    size="medium"
                                                    position="bottom"
                                                >
                                                    {(isOpen) => (
                                                        <DriverInfo
                                                            driverKey={event?.driverId}
                                                            openTooltip={isOpen}
                                                            driverId={event?.driverId}
                                                        />
                                                    )}
                                                </DynamicTooltip>
                                            ) : <p
                                                onClick={() => {
                                                    dispatch(openVideoAssignDriverModal({ ...event, unitId: event?.deviceIdGps }));
                                                    handleClose();
                                                }}
                                                className="edit-event-link">
                                                {messages['driver_performance_unassigned']}
                                            </p>
                                        }
                                    </td>
                                </tr>
                                <tr className="edit-event-modal-info__row">
                                    <td className="edit-event-modal-info__label">{messages["driver_performance_events_drawer_asset"]}:</td>
                                    <td className="edit-event-modal-info__value">
                                        <DynamicTooltip
                                            key="assetInfo"
                                            trigger={<p style={{ fontFamily: 'Outfit, sans-serif', cursor: 'pointer', fontSize: 14, fontWeight: 400, borderBottom: '1px dashed #595151cc', display: 'inline-block', margin: 0, padding: 0, lineHeight: 1.4 }}>{data?.assetName || 'N/A'}</p>}
                                            openOn="click"
                                            size="medium"
                                            position="bottom"
                                        >
                                            {(isOpen) => (
                                                <AssetInfo
                                                    assetKey={event?.assetId}
                                                    openTooltip={isOpen}
                                                    assetId={event?.assetId}
                                                    assetName={data?.assetName}
                                                />
                                            )}
                                        </DynamicTooltip>
                                    </td>
                                </tr>
                            </tbody>
                        </table> : <div style={{ display: 'flex', justifyContent: 'center', opacity: 0.6, marginTop: 55 }}>{messages["driver_performance_events_drawer_no_data"]}</div>
                }
                <div className="edit-event-modal-video-container">
                    {!loading && data && (data?.eventCode !== "GpsData.Moving") && (
                        // Handle LightMetrics devices
                        data.type === "LightMetrics" ? (
                            (() => {
                                try {
                                    const url = data.formatDeviceData?.url;
                                    const urlExpiration = data.formatDeviceData?.urlExpiration;
                                    const mediaType = data.formatDeviceData?.mediaType;
                                    const isVideoExpired = urlExpiration && new Date(urlExpiration) < new Date();
                                    const defaultImagePath = locale === "en" ? "/assets/images/no_video_available.png" : "/assets/images/no_video_available_es.png";

                                    // No URL and no expiration
                                    if (!url && !urlExpiration) {
                                        return (
                                            <div className="edit-event-modal-no-media">
                                                <img src={defaultImagePath} alt="Video Expired" />
                                            </div>
                                        );
                                    }

                                    // Video expired
                                    if (isVideoExpired) {
                                        return (
                                            <div className="edit-event-modal-no-media">
                                                <img src={defaultImagePath} alt="Video Expired" />
                                            </div>
                                        );
                                    }

                                    // Validate URL exists for rendering
                                    if (!url) {
                                        return (
                                            <div className="edit-event-modal-no-media">
                                                <img src={defaultImagePath} alt="No URL Available" />
                                            </div>
                                        );
                                    }

                                    // Picture/Image type
                                    if (mediaType === "picture") {
                                        return (
                                            <>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: "400px", backgroundColor: '#000' }}>
                                                    <img
                                                        style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', cursor: 'pointer' }}
                                                        src={url}
                                                        alt="Event Snapshot"
                                                        onClick={() => setOpenImageModal(true)}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            const container = e.target.parentElement;
                                                            if (container) {
                                                                container.style.background = '#e5e5e5';
                                                                container.innerHTML = `<img style="width: 160px" src="${defaultImagePath}" alt="Error loading image" />`;
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {openImageModal && url && (
                                                    <div
                                                        className="edit-event-modal-image-fullscreen"
                                                        onClick={() => setOpenImageModal(false)}
                                                    >
                                                        <img
                                                            src={url}
                                                            alt="Event Snapshot Full"
                                                            onError={(e) => {
                                                                setOpenImageModal(false);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        );
                                    }

                                    // Video type
                                    return (
                                        <div className="edit-event-modal-video-player">
                                            <video
                                                controls
                                                onError={(e) => {
                                                    console.error('Error loading video:', url);
                                                }}
                                            >
                                                <source src={url} type="video/mp4" />
                                                {messages["videoMainView_browserNotSupport"] || "Your browser does not support the video tag."}
                                            </video>
                                        </div>
                                    );
                                } catch (error) {
                                    const defaultImagePath = locale === "en" ? "/assets/images/no_video_available.png" : "/assets/images/no_video_available_es.png";
                                    return (
                                        <div className="edit-event-modal-no-media">
                                            <img src={defaultImagePath} alt="Error loading media" />
                                        </div>
                                    );
                                }
                            })()
                        ) : (
                            // Handle VidFleet and SmartWitness devices
                            ["VIDFLEET", "SMARTWITNESS"].includes(data?.type?.toUpperCase()) ? (
                                data.type === "SmartWitness" || data.videoOptions?.deviceModel === "SmartWitness" ? (
                                    <div className="edit-event-modal-smartwitness" style={{ height: 350 }}>
                                        <SmartWitnessSnapshot
                                            vehicleTrail={data.deviceData}
                                            response={data}
                                            height={350}
                                            indicators={false}
                                        />
                                    </div>
                                ) : (
                                    <div className="edit-event-modal-vidfleet">
                                        <VidFleetVideo {...data.videoOptions} />
                                    </div>
                                )
                            ) : (
                                <div className="edit-event-modal-no-media">
                                    <img
                                        src={locale === "en" ? "/assets/images/no_video_available.png" : "/assets/images/no_video_available_es.png"}
                                        alt="No video data available"
                                    />
                                </div>
                            )
                        )
                    )}
                </div>
                <div className="edit-event-modal-map-container">
                    {
                        !loading && data &&
                        <Map
                            data={mapData}
                            zoom={15}
                            height={200}
                        />
                    }
                </div>
                {!loading && event && (
                    <div className="edit-event-modal-additional-info">
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', height: 28 }}>
                                <div className="input-gtc-rc-label" style={{ width: 'auto' }}><label className="label-gtc-rc">{messages['driver_performance_events_assingStatus_status']}</label></div>
                                {eventIcon && (
                                    <span className="event-icon-container" style={{ marginLeft: 5 }}>
                                        {eventIcon}
                                    </span>
                                )}
                            </div>
                            {isDisputed && (
                                <div className="edit-event-modal-info__row" style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                    <div className="edit-event-modal-info__label">
                                        <label className="label-gtc-rc">{messages['driver_performance_events_drawer_driver_challenged']}</label>:
                                    </div>
                                    <div className="edit-event-modal-info__value" style={{ marginLeft: 8 }}>
                                        <div className="edit-event-modal-disputed-warning">
                                            <img style={{ width: 20, height: 20 }} src={`/assets/images/dr_warning.svg`} alt="error" />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <Select
                                id="status"
                                options={(localStatuses || []).map((item) => ({ ...item, label: messages[`driver_performance_${item.entityId}`] || item?.label })).filter(status => ["falsepositive", "noissue", "verified", "highlightcoaching"].includes(status.entityId))}
                                textField="label"
                                valueField="entityId"
                                placeholder={messages['driver_performance_events_assingStatus_selectStatus']}
                                value={status}
                                onChange={(value) => {
                                    setStatus(value);
                                }}
                                isLoading={loadingStatuses}
                            />
                        </div>

                        {/* Comments Section */}
                        <div>
                            <div className="input-gtc-rc-label"><label className="label-gtc-rc">{messages['driver_performance_comments'] || 'Comments'}</label></div>
                            {/* Comments List */}
                            {eventComments.length > 0 ? (
                                <Grid container direction="column" spacing={0} className="comments-list-container" style={{ backgroundColor: '#fafafa', padding: 5, minWidth: 275 }}>
                                    {eventComments.map((comment, index) => {
                                        const createUser = chatUsers?.find(x => x.authId == comment.createdBy);

                                        return (
                                            <React.Fragment key={comment.id}>
                                                <Grid item xs={12}>
                                                    <div className="comment-box">
                                                        <div className="comment-header">
                                                            <Typography variant="body2" className="comment-author">
                                                                {createUser?.label || comment.createdBy}
                                                            </Typography>
                                                            {comment.createdOn && (
                                                                <div className="event-container-timezone">
                                                                    <Timezone
                                                                        date={comment.createdOn}
                                                                        dateFormat={dateformat}
                                                                        timeFormat="hh:mm a"
                                                                        timezone={timeZone}
                                                                        className="grey-color"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Typography variant="body2" className="comment-content">
                                                            {comment.comment}
                                                        </Typography>
                                                    </div>
                                                </Grid>
                                                {index < eventComments.length - 1 && (
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ my: 1 }} />
                                                    </Grid>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Grid>
                            ) : (
                                <div className="flex-center min-height-200" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {messages['driver_performance_no_comments'] || 'No comments yet'}
                                    </Typography>
                                </div>
                            )}

                            {/* Comment Input */}
                            <div className="comment-input-container" style={{ paddingTop: 12, paddingBottom: 12, borderTop: '1px solid #e0e0e0', marginTop: 16 }}>
                                {!editingComment ? (
                                    <div
                                        onClick={() => setEditingComment(true)}
                                        className="comment-placeholder"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            borderRadius: 4,
                                            cursor: 'text',
                                            backgroundColor: '#fff',
                                            color: '#999',
                                            fontSize: 14,
                                            transition: 'border-color 0.2s',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {messages['driver_performance_add_comment'] || 'Add a comment'}...
                                    </div>
                                ) : (
                                    <TextArea
                                        id="comments"
                                        value={commentText}
                                        onChange={(value) => setCommentText(value)}
                                        className="comment-textarea"
                                        autoFocus
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <NotificationContainer />
        </Modal >
    );
};

export default EditEvent;